import type {
  BidFindingSeverity,
  BidFindingType,
  RequirementComplianceStatus,
  StaffingFindingType,
} from "../../types"

import { AlertTriangle, CheckCircle2, Info, MinusCircle, XCircle } from "@/components/icons"

export const SEVERITY_CONFIG: Record<
  BidFindingSeverity,
  { labelKey: string; icon: React.ElementType; className: string }
> = {
  critical: {
    labelKey: "tender.bidReview.severity.critical",
    className: "bg-destructive/10 text-destructive border-destructive",
    icon: XCircle,
  },
  major: {
    labelKey: "tender.bidReview.severity.major",
    className: "bg-warning/10 text-warning-foreground border-warning",
    icon: AlertTriangle,
  },
  minor: {
    labelKey: "tender.bidReview.severity.minor",
    className: "bg-accent/50 text-accent-foreground border-accent",
    icon: Info,
  },
  info: {
    labelKey: "tender.bidReview.severity.info",
    className: "bg-muted/60 text-muted-foreground border-border",
    icon: Info,
  },
}

export const FINDING_TYPE_LABEL_KEY: Record<BidFindingType, string> = {
  missing_requirement: "tender.bidReview.findingType.missing_requirement",
  incomplete_requirement: "tender.bidReview.findingType.incomplete_requirement",
  missing_deliverable: "tender.bidReview.findingType.missing_deliverable",
  missing_role: "tender.bidReview.findingType.missing_role",
  risk: "tender.bidReview.findingType.risk",
  suggestion: "tender.bidReview.findingType.suggestion",
  strength: "tender.bidReview.findingType.strength",
}

export const STAFFING_FINDING_TYPE_LABEL_KEY: Record<StaffingFindingType, string> = {
  understaffed: "tender.bidReview.staffingFindingType.understaffed",
  overstaffed: "tender.bidReview.staffingFindingType.overstaffed",
  missing_hard_req: "tender.bidReview.staffingFindingType.missing_hard_req",
  missing_soft_req: "tender.bidReview.staffingFindingType.missing_soft_req",
  all_overqualified: "tender.bidReview.staffingFindingType.all_overqualified",
  no_candidates_found: "tender.bidReview.staffingFindingType.no_candidates_found",
}

export const COMPLIANCE_CONFIG: Record<
  RequirementComplianceStatus,
  {
    labelKey: string
    icon: React.ElementType
    rowClass: string
    iconClass: string
    badgeClass: string
  }
> = {
  met: {
    labelKey: "tender.bidReview.compliance.met",
    icon: CheckCircle2,
    rowClass: "border-success/30 bg-success/[0.03] dark:border-success dark:bg-success/[0.05]",
    iconClass: "text-success dark:text-success",
    badgeClass: "border-success bg-success/10 text-success",
  },
  partial: {
    labelKey: "tender.bidReview.compliance.partial",
    icon: MinusCircle,
    rowClass: "border-warning/30 bg-warning/[0.03] dark:border-warning dark:bg-warning/[0.05]",
    iconClass: "text-warning dark:text-warning-foreground",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
  missing: {
    labelKey: "tender.bidReview.compliance.missing",
    icon: XCircle,
    rowClass:
      "border-destructive/30 bg-destructive/[0.03] dark:border-destructive dark:bg-destructive/[0.04]",
    iconClass: "text-destructive dark:text-destructive",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
}

export function scoreColor(score: number, max = 100): string {
  const ratio = max > 0 ? score / max : 0
  if (ratio >= 0.8) return "text-success dark:text-success"
  if (ratio >= 0.6) return "text-warning dark:text-warning-foreground"
  return "text-destructive dark:text-destructive"
}
